import { useTranslation } from 'react-i18next'
import { KatexRenderer } from '@quebon/shared-learning/utils'
import { KatexText } from '@quebon/shared-learning'
import { parseSelectTextContents, parseSelectTextOptionModels } from '@quebon/shared-learning/components/quiz-types/hooks/use-select-text-quiz'
import { PairingAnswerDiagram } from './PairingAnswerDiagram'
import { displayCorrectAnswer } from '../component-details/parts/QuestionResultCard'

function record(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function answerKey(value: unknown): string {
  const answer = record(value)?.answer ?? value
  return typeof answer === 'string' || typeof answer === 'number' ? String(answer) : ''
}

function optionKey(value: unknown): string {
  return answerKey(record(value)?.key)
}

const circledNumber = (index: number) => index < 20 ? String.fromCodePoint(0x2460 + index) : String(index + 1)

export function GradingCorrectAnswer({ correctAnswer, questionSnapshot, mobile = false }: {
  correctAnswer: unknown
  questionSnapshot: unknown
  mobile?: boolean
}) {
  const { t } = useTranslation('coaching')
  const snapshot = record(questionSnapshot)
  const values = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer]

  if (snapshot?.type === 'select_text') {
    const contents = parseSelectTextContents(typeof snapshot.problem === 'string' ? snapshot.problem : '')
    const labels = values.map((value, index) => {
      const raw = answerKey(value)
      const selectedIndex = raw.trim() ? Number(raw) : -1
      if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || contents[index] == null) return '—'
      const option = parseSelectTextOptionModels(contents[index])
        .find((candidate) => candidate.rawIndex === selectedIndex && candidate.isSelectable)
      return option?.label ?? '—'
    })
    return <KatexText text={labels.join(', ')} />
  }

  if (snapshot?.type === 'ordering') {
    const choices: unknown[] = Array.isArray(snapshot.choices) ? snapshot.choices : []
    const order = values.map((value) => {
      const key = answerKey(value)
      const index = key ? choices.findIndex((choice) => optionKey(choice) === key) : -1
      return index >= 0 ? circledNumber(index) : '—'
    })
    return <>{order.join(' → ')}</>
  }

  if (snapshot?.type === 'pairing') {
    const pairs = record(snapshot.pairs)
    const left = Array.isArray(pairs?.left) ? pairs.left : []
    const right = Array.isArray(pairs?.right) ? pairs.right : []
    const matches = left.map((_, index) => {
      const key = answerKey(values[index])
      return key ? right.findIndex((option) => optionKey(option) === key) : -1
    })
    return (
      <div className="flex flex-col gap-3">
        {!mobile && <span className="inline-flex flex-wrap gap-2">
          {matches.map((rightIndex, index) => {
            return (
              <span key={index} className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1">
                <span className="text-black">{index + 1}</span>
                <span className="text-black">→</span>
                <span className="text-red-600">{rightIndex >= 0 ? rightIndex + 1 : '—'}</span>
              </span>
            )
          })}
          {left.length === 0 ? t('gradingMode.noCorrectAnswer') : null}
        </span>}
        {matches.some((index) => index >= 0) ? <PairingAnswerDiagram matches={matches} rightCount={right.length} interactive={!mobile} />
          : mobile ? t('gradingMode.noCorrectAnswer') : null}
      </div>
    )
  }

  if (snapshot?.type === 'multi_choice' || snapshot?.type === 'image_select') {
    const candidates = snapshot.choices ?? snapshot.answers
    const choices: unknown[] = Array.isArray(candidates) ? candidates : []
    const random = snapshot.type === 'multi_choice' && Array.isArray(snapshot.random) ? snapshot.random : []
    const displayed = random.length > 0
      ? random.flatMap((index) => typeof index === 'number' && choices[index] != null ? [choices[index]] : [])
      : choices
    const indices = values.map((value) => {
      const key = answerKey(value)
      if (!key) return -1
      let originalIndex = choices.findIndex((option) => optionKey(option) === key)
      // MultiChoicePrint also accepts a 1-based original choice index from the solve API.
      if (originalIndex < 0 && snapshot.type === 'multi_choice') {
        const number = Number(key)
        if (Number.isInteger(number) && number >= 1 && number <= choices.length) originalIndex = number - 1
      }
      return originalIndex < 0 ? -1 : displayed.indexOf(choices[originalIndex])
    })
    return <>{indices.map((index) => index >= 0 ? circledNumber(index) : '—').join(', ')}</>
  }

  return <KatexRenderer text={displayCorrectAnswer(correctAnswer, questionSnapshot, {
    true: t('solutionPreview.booleanTrue'),
    false: t('solutionPreview.booleanFalse'),
  })} />
}
